import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { COLORES, ROTULOS } from "../../../shared/piezas/index.ts";
import type { Dibujo, Elemento } from "../../../shared/piezas/index.ts";
import { FICHA } from "./catalogo.ts";

/**
 * Pinta en Nexo un dibujo de app/shared/piezas con JSX, nunca como HTML crudo (diseño 4a §2.1 y §2.2; el mismo trato que
 * <Pieza> de Argus Team). Cada <text> lleva fijados la familia, el peso, el interletrado 0 y text-transform:none: lo que se pinta es
 * lo que la pieza midió. El dibujo se pinta sólo con Instrument Sans cargada (los WOFF de app/shared/piezas/fuente/); si en 3 s no
 * está, se dice (texto 53b) y la ficha muestra lo mismo en texto abajo. Los elementos con fuente son botones «Ver la fuente».
 * Los colores son los de la guía (COLORES de las piezas): Nexo no tiene variables de la hoja de Argus Team.
 */

const ANCLA = { inicio: "start", medio: "middle", fin: "end" } as const;
export const FAMILIA = "Instrument Sans";
/** Cuánto se espera la fuente antes de decir que no se pudo mostrar el dibujo (diseño §2.2). */
export const ESPERA_FUENTE_MS = 3000;

/**
 * ¿Está Instrument Sans lista para estos textos? `load` con los textos del dibujo (para que cargue también el juego extendido) y
 * luego `check`, porque `load` no falla si la familia no existe.
 */
async function fuenteLista(textos: string, pesos: number[], plazo = ESPERA_FUENTE_MS): Promise<boolean> {
  const fonts = (typeof document !== "undefined" ? document.fonts : undefined) as FontFaceSet | undefined;
  if (!fonts || typeof fonts.load !== "function" || typeof fonts.check !== "function") return false;
  const specs = [...new Set(pesos)].map((p) => `${String(p)} 13px "${FAMILIA}"`);
  const carga = Promise.all(specs.map((s) => fonts.load(s, textos)))
    .then((cargadas) => cargadas.every((c) => c.some((f) => f.family.replace(/["']/g, "") === FAMILIA)) && specs.every((s) => fonts.check(s, textos)))
    .catch(() => false);
  let reloj: ReturnType<typeof setTimeout> | undefined;
  const tope = new Promise<boolean>((resolve) => { reloj = setTimeout(() => { resolve(false); }, plazo); });
  try {
    return await Promise.race([carga, tope]);
  } finally {
    clearTimeout(reloj);
  }
}

interface Props {
  /** El dibujo a un ancho dado (la pieza acomoda sus textos a ese ancho). */
  dibujar(ancho: number): Dibujo | null;
  /** «Ver la fuente» de un elemento: el índice en `dibujo.fuentes` y el elemento (que dice de qué sancionado es). */
  onFuente?(indice: number, dibujo: Dibujo, elemento: Elemento): void;
  /** De quién es lo que abre un elemento, para el nombre accesible de su botón («Ver la fuente: …»). */
  deQuien?(elemento: Elemento): string | null;
}

/** Ancho mínimo: en un lugar más angosto el dibujo no se encoge por debajo de 360 px (el menor que prueba P1) y se recorre de lado. */
const ANCHO_MINIMO = 360;

export function Pieza({ dibujar, onFuente, deQuien }: Props) {
  const caja = useRef<HTMLDivElement>(null);
  const [ancho, setAncho] = useState(720);
  useLayoutEffect(() => {
    const el = caja.current;
    if (!el) return;
    const medir = () => {
      const w = Math.floor(el.clientWidth);
      if (w > 0) setAncho((a) => (Math.abs(a - w) >= 8 ? w : a));
    };
    medir();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => { ro.disconnect(); };
  }, []);
  const dibujo = useMemo(() => dibujar(Math.max(ancho, ANCHO_MINIMO)), [dibujar, ancho]);
  const textos = useMemo(() => (dibujo ? dibujo.elementos.flatMap((e) => (e.tipo === "texto" ? [e.contenido] : [])).join(" ") : ""), [dibujo]);
  const pesos = useMemo(() => (dibujo ? dibujo.elementos.flatMap((e) => (e.tipo === "texto" ? [e.peso] : [])) : []), [dibujo]);
  const [fuente, setFuente] = useState<"cargando" | "lista" | "sin_fuente">("cargando");
  useEffect(() => {
    if (!dibujo) return;
    let vivo = true;
    void fuenteLista(textos, pesos).then((ok) => { if (vivo) setFuente((f) => (f === "lista" && ok ? f : ok ? "lista" : "sin_fuente")); });
    return () => { vivo = false; };
  }, [dibujo, textos, pesos]);

  const desborda = !!dibujo && fuente === "lista" && dibujo.ancho > ancho + 1;
  return (
    <div ref={caja} className="nx-pieza" {...(desborda && dibujo ? { tabIndex: 0, role: "region", "aria-label": dibujo.descripcion } : {})}>
      {dibujo && fuente === "sin_fuente" ? <p className="nx-tenue" role="note">{ROTULOS.sinDibujo}</p> : null}
      {dibujo && fuente === "lista" ? <Svg dibujo={dibujo} {...(onFuente ? { onFuente } : {})} {...(deQuien ? { deQuien } : {})} /> : null}
    </div>
  );
}

function Svg({ dibujo, onFuente, deQuien }: { dibujo: Dibujo; onFuente?(indice: number, dibujo: Dibujo, elemento: Elemento): void; deQuien?(e: Elemento): string | null }) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const marcas = [...new Set(dibujo.elementos.flatMap((e) => (e.tipo === "linea" && e.flecha === "fin" ? [e.borde] : [])))];
  const raiz = useRef<SVGSVGElement>(null);

  // Comprobación de desarrollo: la caja real de cada texto no debe pasar de la que la pieza reservó (diseño §2.2).
  useEffect(() => {
    if (!import.meta.env.DEV || !raiz.current) return;
    for (const t of Array.from(raiz.current.querySelectorAll<SVGTextElement>("text[data-ancho]"))) {
      try {
        const real = t.getBBox().width, reservado = Number(t.dataset["ancho"]);
        if (real > reservado + 0.5) console.warn(`pieza: «${t.textContent ?? ""}» mide ${real.toFixed(1)} px y la pieza reservó ${reservado.toFixed(1)} px`);
      } catch { /* sin geometría (pestaña oculta) */ }
    }
  }, [dibujo]);

  const boton = (e: Elemento, hijo: JSX.Element, respaldo: string) => {
    const i = "fuente" in e ? e.fuente : undefined;
    if (i === undefined || !onFuente) return hijo;
    const de = deQuien?.(e);
    const etiqueta = de ? `${FICHA.verFuente}: ${de}` : respaldo;
    const abrir = () => { onFuente(i, dibujo, e); };
    const tecla = (ev: KeyboardEvent<SVGGElement>) => {
      if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); abrir(); }
    };
    return <g key={e.id} role="button" tabIndex={0} className="nx-pieza-fuente" aria-label={etiqueta} onClick={abrir} onKeyDown={tecla}>{hijo}</g>;
  };

  return (
    <svg ref={raiz} className="nx-pieza-svg" xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 ${String(dibujo.ancho)} ${String(dibujo.alto)}`} width={dibujo.ancho} height={dibujo.alto}
      role="group" aria-label={dibujo.descripcion} style={{ letterSpacing: 0, textTransform: "none", display: "block" }}>
      <defs>
        {marcas.map((t) => (
          <marker key={t} id={`punta-${uid}-${t}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0 0 L10 5 L0 10 z" fill={COLORES[t]} />
          </marker>
        ))}
        <pattern id={`rayado-${uid}`} width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="8" stroke="#D6D1C3" strokeWidth="3" />
        </pattern>
      </defs>
      {dibujo.elementos.map((e) => {
        switch (e.tipo) {
          case "rect":
            return boton(e, <rect key={e.id} data-id={e.id} x={e.caja.x} y={e.caja.y} width={e.caja.w} height={e.caja.h} rx={e.radio} fill={COLORES[e.relleno]}
              stroke={COLORES[e.borde]} strokeWidth={1.5} {...(e.discontinuo ? { strokeDasharray: "4 3" } : {})} />, FICHA.verFuente);
          case "circulo":
            return <circle key={e.id} data-id={e.id} cx={e.cx} cy={e.cy} r={e.r} fill={COLORES[e.relleno]} stroke={COLORES[e.borde]} strokeWidth={1.5} />;
          case "linea":
            return boton(e, <polyline key={e.id} data-id={e.id} points={e.puntos.map(([x, y]) => `${String(x)},${String(y)}`).join(" ")} fill="none" stroke={COLORES[e.borde]} strokeWidth={e.grosor}
              {...(e.discontinua ? { strokeDasharray: "5 4" } : {})} {...(e.flecha === "fin" ? { markerEnd: `url(#punta-${uid}-${e.borde})` } : {})} />, FICHA.verFuente);
          case "rayado":
            return <rect key={e.id} data-id={e.id} data-hueco={e.hueco} x={e.caja.x} y={e.caja.y} width={e.caja.w} height={e.caja.h} fill={`url(#rayado-${uid})`} />;
          case "texto":
            return boton(e, (
              <text key={e.id} data-id={e.id} data-ancho={e.caja.w} x={e.x} y={e.y} textAnchor={ANCLA[e.ancla]} fontFamily={FAMILIA} fontSize={e.tam} fontWeight={e.peso}
                letterSpacing="0" style={{ textTransform: "none" }} fill={COLORES[e.tinta]}>
                {e.completo !== e.contenido ? <title>{e.completo}</title> : null}
                {e.contenido}
              </text>
            ), `${FICHA.verFuente}: ${e.completo}`);
        }
      })}
    </svg>
  );
}
